import type { ReactNode } from "react";
import type { CalendarDate } from "@pack-it-up/shared";
import type { Assignment, PackingItem, RelativeOffset, TodoItem } from "@pack-it-up/core";
import type { Person } from "./people.js";
import { tagKey } from "@pack-it-up/core";
import { ItemComposer, type ItemComposerDraft } from "./ItemComposer.js";

// Tag arrays arrive from the composer already normalized (sorted, deduped), so a
// casefolded join is enough to tell "changed" from "same".
const sameTags = (a: string[], b: string[]): boolean =>
  a.map(tagKey).join("\u0000") === b.map(tagKey).join("\u0000");

// The whole-row item composer (Doc 12 §1): clicking a row reveals the shared
// `ItemComposer` body pre-filled from the item/todo. Commit on Enter (in any field),
// explicit Save button, or when focus leaves the card; Cancel button or Esc cancels; a
// blank name reverts. Assignee changes apply live via `onSetAssignment` (not buffered in
// the draft) — unchanged from before the T-G rework.

export interface ItemPatchDraft {
  name?: string;
  description?: string | null;
  quantity?: number;
  /** `null` clears every tag (T29). */
  tags?: string[] | null;
}

export const ItemEditForm = ({
  item,
  people,
  canEdit,
  tagPalette,
  onCommit,
  onCancel,
  onSetAssignment,
  onResetToTemplate,
  onDelete,
}: {
  item: PackingItem;
  people: Person[];
  tagPalette?: string[];
  canEdit: boolean;
  onCommit: (patch: ItemPatchDraft) => void;
  onCancel: () => void;
  onSetAssignment: (assignment: Assignment) => void;
  onResetToTemplate?: () => void;
  onDelete?: () => void;
}): ReactNode => {
  const commit = (draft: ItemComposerDraft): void => {
    const patch: ItemPatchDraft = {};
    const nm = draft.name.trim();
    if (nm !== "" && nm !== item.name) patch.name = nm;
    const desc = draft.notes.trim();
    if (desc !== (item.description ?? "")) patch.description = desc === "" ? null : desc;
    if (draft.quantity !== item.quantity) patch.quantity = draft.quantity;
    if (!sameTags(draft.tags, item.tags ?? [])) patch.tags = draft.tags.length === 0 ? null : draft.tags;
    // Assignment applies live (below), independent of this buffered patch.
    if (draft.assignment !== item.assignment) onSetAssignment(draft.assignment);
    onCommit(patch);
  };

  return (
    <ItemComposer
      ariaLabel={`Edit ${item.name}`}
      primaryLabel="Save"
      inset
      people={people}
      initial={{
        name: item.name,
        quantity: item.quantity,
        assignment: item.assignment,
        notes: item.description ?? "",
        ...(item.tags !== undefined ? { tags: item.tags } : {}),
      }}
      {...(tagPalette !== undefined ? { tagPalette } : {})}
      onCommit={commit}
      onCancel={onCancel}
      {...(canEdit && onResetToTemplate !== undefined ? { onResetToTemplate } : {})}
      {...(canEdit && onDelete !== undefined ? { onDelete } : {})}
    />
  );
};

export interface TodoPatchDraft {
  name?: string;
  description?: string | null;
  dueDate?: null;
  /** `null` clears every tag (T29). */
  tags?: string[] | null;
}

// A manual due-date *set* pins it (onPinDueDate → todosStore.pinDueDate); clearing it
// goes through the scalar patch as `dueDate: null` (Doc 12 §1.3).
export const TodoEditForm = ({
  todo,
  people,
  canEdit,
  tagPalette,
  dueMode = "date",
  onCommit,
  onCancel,
  onPinDueDate,
  onSetRelativeDue,
  onSetAssignment,
  onDelete,
}: {
  todo: TodoItem;
  people: Person[];
  tagPalette?: string[];
  canEdit: boolean;
  // Issue #188 — a template's to-dos are due relative to the trip's start date, so the
  // composer offers the offset control instead of a date picker (Doc 07 §6).
  dueMode?: "date" | "relative";
  onCommit: (patch: TodoPatchDraft) => void;
  onCancel: () => void;
  onPinDueDate: (date: CalendarDate) => void;
  onSetRelativeDue?: (offset: RelativeOffset | null) => void;
  onSetAssignment: (assignment: Assignment) => void;
  onDelete?: () => void;
}): ReactNode => {
  const commit = (draft: ItemComposerDraft): void => {
    const patch: TodoPatchDraft = {};
    const nm = draft.name.trim();
    if (nm !== "" && nm !== todo.name) patch.name = nm;
    const desc = draft.notes.trim();
    if (desc !== (todo.description ?? "")) patch.description = desc === "" ? null : desc;
    if (dueMode === "relative") {
      // The offset is its own field-level write, like a pinned date — never part of the
      // scalar patch.
      const next = draft.relativeDueOffset ?? null;
      const cur = todo.relativeDueOffset ?? null;
      const same =
        next === null && cur === null
          ? true
          : next !== null && cur !== null && next.value === cur.value && next.unit === cur.unit;
      if (!same) onSetRelativeDue?.(next);
    } else {
      const cur = todo.dueDate ?? "";
      if (draft.dueDate !== cur) {
        if (draft.dueDate === "") patch.dueDate = null;
        else onPinDueDate(draft.dueDate as CalendarDate);
      }
    }
    if (!sameTags(draft.tags, todo.tags ?? [])) patch.tags = draft.tags.length === 0 ? null : draft.tags;
    if (draft.assignment !== todo.assignment) onSetAssignment(draft.assignment);
    onCommit(patch);
  };

  return (
    <ItemComposer
      ariaLabel={`Edit ${todo.name}`}
      primaryLabel="Save"
      variant="todo"
      dueMode={dueMode}
      inset
      people={people}
      initial={{
        name: todo.name,
        assignment: todo.assignment,
        notes: todo.description ?? "",
        dueDate: todo.dueDate ?? "",
        ...(todo.relativeDueOffset !== undefined ? { relativeDueOffset: todo.relativeDueOffset } : {}),
        ...(todo.tags !== undefined ? { tags: todo.tags } : {}),
      }}
      {...(tagPalette !== undefined ? { tagPalette } : {})}
      onCommit={commit}
      onCancel={onCancel}
      {...(canEdit && onDelete !== undefined ? { onDelete } : {})}
    />
  );
};
